import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { TagClient } from '@app-services/api/clients/tag.client';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
} from '@app-types/base/base';
import { GetByTypeRequest, TagContract } from '@app-types/api/tag';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { DrawerService } from '@app-services/drawer.service';
import {
  MAIL_ACCOUNT_ID,
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { MailAccountTagCreateDialogComponent } from '@app-components/common/mail-account-tag-create-dialog/mail-account-tag-create-dialog.component';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { FormatTagsSearchOrders } from '@app-pipes/format-tags-search-orders.pipe';
import { TagSearchOrder, TagType } from '@app-types/enums/tags';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { NgTemplateOutlet } from '@angular/common';
import { TagComponentComponent } from '@app-components/common/tag-component/tag-component.component';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-mail-account-tags',
  templateUrl: './mail-account-tags.component.html',
  styleUrls: ['./mail-account-tags.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatTooltipModule,
    MatCardModule,
    MailAccountTagCreateDialogComponent,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
    NgTemplateOutlet,
    TagComponentComponent,
  ],
})
export class MailAccountTagsComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public mailAccountId: number;

  public isLoading = true;
  public isError = false;

  public searchValue = '';
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  public totalSize = 0;
  public searchOrder: TagSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterFields<TagSearchOrder> = {
    ...defaultFilter,
  };
  public isExporting = false;
  public dataSource = new MatTableDataSource<TagContract>([]);
  public displayedColumns: string[] = ['tag', 'actions'];
  private useMobileViewSubscription: Subscription;
  public useMobileView = false;
  private formatTagsSearchOrders = new FormatTagsSearchOrders();
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  editingTag: TagContract | null = null;
  @ViewChild('addDrawer') addDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public matchError: MatchError,
    private route: ActivatedRoute,
    private mobileObserverService: MobileObserverService,
    private tagClient: TagClient,
    public dialog: MatDialog,
    private drawerService: DrawerService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.mailAccountId = parseInt(
      this.route.snapshot.paramMap.get(MAIL_ACCOUNT_ID) as string,
      10
    );

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatTagsSearchOrders.transform(key),
        name: key,
      }));

    await this.loadTags(true);
  }

  public async loadTags(fetchCounter: boolean): Promise<void> {
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const nullableSearchValue = this.searchValue ? this.searchValue : null;

      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: nullableSearchValue,
        type: TagType.Email,
      };
      const response = await this.tagClient.searchForMailAccount(request);
      this.dataSource.data =
        this.offset === 0
          ? response.data
          : [...this.dataSource.data, ...response.data];
      if (fetchCounter) {
        const countRequest = new GetByTypeRequest(
          this.mailAccountId,
          TagType.Email,
          nullableSearchValue
        );
        const counterResponse =
          await this.tagClient.getSearchCountForMailAccount(countRequest);
        this.totalSize = counterResponse.result;
      }
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public openCreateModelForm(): void {
    this.drawerService.openDrawer(this.addDrawer);
  }

  public async onCreateTag(): Promise<void> {
    await this.onSearch();
  }

  async onDeleteBtnClicked(tagId: number): Promise<void> {
    await this.tagClient.delete({ id: tagId });
  }

  async deleteTag(tagId: number, tag: string): Promise<void> {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'confirmTagDeletion',
        subTitles: [
          {
            title: 'tagDeletion',
            subTitle: tag,
          },
        ],
        onDelete: async () => await this.onDeleteBtnClicked(tagId),
      },
    });

    dialogRef.afterClosed().subscribe(async x => {
      if (x?.isDeleted) {
        await this.onSearch();
      }
    });
  }

  editTag(tag: TagContract): void {
    this.editingTag = tag;
    this.drawerService.openDrawer(this.addDrawer);
  }

  public onCloseEditModalForm(): void {
    this.editingTag && (this.editingTag = null);
  }

  public countTo(): string {
    return this.dataSource.data.length.toString();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadTags(false);
  }

  async refreshData(): Promise<void> {
    await this.loadTags(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadTags(fetchSearchCounter);
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as TagSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatTagsSearchOrders.transform(sort.active)
      : TagSearchOrder.OrderByTagId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    this.isExporting = true;
    try {
      const fileResponse = await this.tagClient.exportExcel(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadTags(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
